import { htmlToSlate } from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { focusFirstInvalidField } from '../focusInvalidField';
import {
    emptyObjectiveFormValues,
    toObjectiveFormValues,
    toObjectiveRequest,
    toSavedKeyResults,
    toSavedResponsibles,
} from '../objectiveFormValues';
import {
    draftObjectiveFormSchema,
    objectiveFormSchema,
    objectiveWithPillarChoiceFormSchema,
    objectiveWithRequiredPillarFormSchema,
} from '../validation';
import { requiresPillar, requiresPillarChoice } from './usePillarChoice';

import type { PillarChoice } from './usePillarChoice';
import type { SavedKeyResult } from '../objectiveFormValues';
import type { ObjectiveFormData } from '../validation';
import type { ObjectiveStatusValue } from '@/constants/ObjectiveStatus';
import type { PeriodOption } from '@/hooks/objectives/usePeriodOptions';
import type { TranslationKey } from '@/i18n-key';
import type { ObjectiveOrDraft } from '@/types/objectives';
import type { FormEvent } from 'react';
import type { FieldErrors } from 'react-hook-form';

import { readRefusal } from '@/api/api';
import { useCreateObjective, useUpdateObjective } from '@/api/objectives';
import { ObjectiveStatus } from '@/constants/ObjectiveStatus';
import { PillarStatus } from '@/constants/PillarStatus';
import { useObjectivesStepBack } from '@/hooks/objectives/useObjectivesStepBack';
import { useError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { objectiveDetailPath, objectivesPath, stepBackState } from '@/navigation/objectivesRoutes';
import i18n from '@/plugins/i18n';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/plugins/Toast';
import { getLocalizedText } from '@/utils/localization';
import { toReaderLocale } from '@/utils/numberFormat';
import { isPublishedObjective } from '@/utils/objectiveStatus';

const HOLDERS_OUT_OF_REACH_MESSAGE: TranslationKey = 'objectives.form.holdersOutOfReach';
const HOLDERS_OUT_OF_REACH_CODE = 'ObjectiveHoldersOutOfReach';
/**
 * The API's refusal to publish a draft whose period was deactivated or completed since it was saved.
 * Unlike the holders refusal, this one is a plain domain validation: the server puts its resource key
 * in the problem document's `detail`, not in a `code` extension.
 */
const PERIOD_NOT_ACTIVE_KEY = 'ObjectiveObjectivePeriodNotActiveError';

interface UseObjectiveFormParams {
    /** Absent when creating an objective. */
    objectiveId?: string;
    existingObjective?: ObjectiveOrDraft;
    prefilledEmployeeId?: string;
    pillarChoice: PillarChoice;
    periodOptions: PeriodOption[];
    defaultPeriodId?: string;
    /** The reader, once known, when a new objective starts as theirs: they start as its holder. */
    selfOwnerId?: string;
}

/**
 * The objective form's values, validation and save: hydration from the objective being edited, the
 * default period of a new one, and the submit that publishes, saves a draft or saves changes.
 */
export const useObjectiveForm = ({
    objectiveId,
    existingObjective,
    prefilledEmployeeId,
    pillarChoice,
    periodOptions,
    defaultPeriodId,
    selfOwnerId,
}: UseObjectiveFormParams) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { canStepBack, stepBackOr } = useObjectivesStepBack();
    const { handleError } = useError();
    const isEditMode = !!objectiveId;

    const createMutation = useCreateObjective();
    const updateMutation = useUpdateObjective();

    // Set by the submit button that was pressed, and read by the resolver: saving a draft only
    // requires a title, publishing requires the whole form.
    const submitStatusRef = useRef<ObjectiveStatusValue>(ObjectiveStatus.PUBLISHED);
    // Held from the first submit to its end. The buttons only disable once the save has rendered,
    // so a second click faster than that would otherwise send the objective twice -- or switch the
    // status under a save still validating.
    const isSubmittingRef = useRef(false);
    const formRef = useRef<HTMLFormElement>(null);

    const selectFormSchema = () => {
        if (submitStatusRef.current === ObjectiveStatus.DRAFT) {
            return draftObjectiveFormSchema;
        }

        if (requiresPillar(pillarChoice)) {
            return objectiveWithRequiredPillarFormSchema;
        }

        return requiresPillarChoice(pillarChoice)
            ? objectiveWithPillarChoiceFormSchema
            : objectiveFormSchema;
    };

    const {
        control,
        getValues,
        handleSubmit,
        reset,
        setError,
        setValue,
        trigger,
        watch,
        formState: { errors, isDirty },
    } = useForm<ObjectiveFormData>({
        resolver: (values, context, options) =>
            zodResolver(selectFormSchema())(values, context, options),
        mode: 'onChange',
        defaultValues: emptyObjectiveFormValues(prefilledEmployeeId),
    });

    // `RichText` is seeded via `defaultValue`, its uncontrolled mode: it reads it once, on mount,
    // and counts what is typed from there. In edit mode it waits for hydration, so its first mount
    // already carries the saved description; replacing the description afterwards re-seeds it and
    // remounts it under a new key.
    const [isHydrated, setIsHydrated] = useState(!isEditMode);
    const [descriptionSeed, setDescriptionSeed] = useState('');
    const [descriptionEditorKey, setDescriptionEditorKey] = useState(0);
    const initialDescriptionValue = useMemo(() => htmlToSlate(descriptionSeed), [descriptionSeed]);

    // The server keeps a responsible who has since left an attribute, so the form never takes
    // them off on its own.
    const [savedResponsibles, setSavedResponsibles] = useState<ReadonlyMap<string, string>>(
        () => new Map(),
    );

    const [savedKeyResults, setSavedKeyResults] = useState<ReadonlyMap<string, SavedKeyResult>>(
        () => new Map(),
    );

    // An empty id means free dates.
    const [selectedPeriodId, setSelectedPeriodId] = useState('');
    // Once the user has touched the picker, the late-arriving default must not override them.
    const [hasPickedPeriod, setHasPickedPeriod] = useState(false);
    const defaultPeriod = periodOptions.find((option) => option.id === defaultPeriodId);

    useEffect(() => {
        if (!isEditMode || !existingObjective) {
            return;
        }

        const values = toObjectiveFormValues(existingObjective);
        setSavedResponsibles(toSavedResponsibles(existingObjective));
        setSavedKeyResults(toSavedKeyResults(existingObjective));
        reset(values);
        setDescriptionSeed(values.description ?? '');
        // A nulled-out period (free dates, or a period since deleted) puts the picker on
        // "custom dates", which shows the editable pickers for the objective's own range.
        setSelectedPeriodId(existingObjective.objectivePeriodId ?? '');
        setIsHydrated(true);
    }, [isEditMode, existingObjective, reset]);

    // A new objective opens on the default period as soon as the periods arrive, unless the user
    // has already touched the picker or set a date of their own.
    useEffect(() => {
        if (isEditMode || !defaultPeriod || hasPickedPeriod) {
            return;
        }

        if (getValues('startDate') || getValues('endDate')) {
            return;
        }

        setSelectedPeriodId(defaultPeriod.id);
        setValue('startDate', defaultPeriod.from);
        setValue('endDate', defaultPeriod.to);
    }, [isEditMode, defaultPeriod, hasPickedPeriod, getValues, setValue]);

    useEffect(() => {
        if (isEditMode || !selfOwnerId) {
            return;
        }

        if (getValues('employeeIds').length === 0) {
            setValue('employeeIds', [selfOwnerId], { shouldValidate: true });
        }
    }, [isEditMode, selfOwnerId, getValues, setValue]);

    // Picking a period copies its dates in (the server does the same on save); picking "custom
    // dates" keeps the current dates as the starting point and hands them back to the user.
    const handlePeriodChange = (periodId: string) => {
        setHasPickedPeriod(true);
        setSelectedPeriodId(periodId);
        const period = periodOptions.find((option) => option.id === periodId);
        if (period) {
            setValue('startDate', period.from, { shouldValidate: true, shouldDirty: true });
            setValue('endDate', period.to, { shouldValidate: true, shouldDirty: true });
        }
    };

    const reseedDescription = () => {
        setDescriptionSeed(getValues('description') ?? '');
        setDescriptionEditorKey((key) => key + 1);
    };

    const chooseSubmitStatus = (status: ObjectiveStatusValue) => {
        if (!isSubmittingRef.current) {
            submitStatusRef.current = status;
        }
    };

    const readSavingStatus = () => {
        if (createMutation.isPending) {
            return createMutation.variables?.status;
        }

        return updateMutation.isPending ? updateMutation.variables?.request.status : undefined;
    };

    const saveObjective = async (data: ObjectiveFormData) => {
        const status = submitStatusRef.current;
        const isDraft = status === ObjectiveStatus.DRAFT;
        // A new objective, or a draft, published: it gets its page only now.
        const isFirstPublication =
            !isDraft && !(existingObjective && isPublishedObjective(existingObjective));
        // A single choice never sends more than one pillar, whatever the form still holds.
        const pillarIds = data.pillarIds ?? [];
        const chosenPillarIds =
            pillarChoice.state === 'ready' && pillarChoice.mode === 'single'
                ? pillarIds.slice(0, 1)
                : pillarIds;
        // A published objective is never born aligned with an inactive pillar: a pillar
        // deactivated since the draft was saved is left out, and the user is told once published.
        const pillarsDeactivatedSince =
            isFirstPublication && pillarChoice.state === 'ready'
                ? pillarChoice.pillars.filter(
                      (pillar) =>
                          chosenPillarIds.includes(pillar.id) &&
                          pillar.status !== PillarStatus.ACTIVE,
                  )
                : [];

        const request = toObjectiveRequest({
            data,
            savedKeyResults,
            status,
            objectivePeriodId: selectedPeriodId,
            pillarIds: chosenPillarIds.filter(
                (pillarId) => !pillarsDeactivatedSince.some((pillar) => pillar.id === pillarId),
            ),
        });

        try {
            const savedObjective = objectiveId
                ? await updateMutation.mutateAsync({ id: objectiveId, request })
                : await createMutation.mutateAsync(request);

            if (isDraft) {
                showSuccessToast({ message: t('objectives.form.draftSaved') });
                stepBackOr({ path: objectivesPath(), replace: true });
                return;
            }

            showSuccessToast({
                message: t(
                    isFirstPublication ? 'objectives.form.published' : 'objectives.form.updated',
                ),
            });
            if (pillarsDeactivatedSince.length > 0) {
                showWarningToast({
                    message: t('objectives.form.pillarsDeactivatedSinceRemoved', {
                        count: pillarsDeactivatedSince.length,
                        names: new Intl.ListFormat(toReaderLocale(i18n.language), {
                            type: 'conjunction',
                        }).format(
                            pillarsDeactivatedSince.map((pillar) => getLocalizedText(pillar.name)),
                        ),
                    }),
                });
            }
            // The form is left behind, not stacked on, so "Retour" never reopens it. A first
            // publication opens the objective's page in the form's place, the screen the creation
            // started from still one step back; saved changes return to where the edit began.
            if (isFirstPublication) {
                navigate({
                    path: objectiveDetailPath(savedObjective.id),
                    state: stepBackState({ canStepBack }),
                    replace: true,
                });
                return;
            }

            stepBackOr({ path: objectiveDetailPath(savedObjective.id), replace: true });
        } catch (error) {
            const refusal = readRefusal(error);

            if (refusal?.code === HOLDERS_OUT_OF_REACH_CODE) {
                const holdersError = { type: 'server', message: HOLDERS_OUT_OF_REACH_MESSAGE };
                setError('employeeIds', holdersError);
                warnInvalidForm({ employeeIds: holdersError });
                return;
            }

            // The period still shows as selected in the form, so a generic failure would give the
            // user nothing to act on: the toast names the period as the reason (#16609).
            if (
                refusal?.reason === PERIOD_NOT_ACTIVE_KEY ||
                refusal?.code === PERIOD_NOT_ACTIVE_KEY
            ) {
                showErrorToast({ message: t('objectives.form.periodNotActive') });
                return;
            }

            handleError({ error });
        }
    };

    // A rule can fail on a field the current mode does not render: with nowhere to show its error,
    // the blocked submit falls back to a toast instead of staying silent.
    const warnInvalidForm = (formErrors: FieldErrors<ObjectiveFormData>) => {
        if (!formRef.current || !focusFirstInvalidField(formErrors, formRef.current)) {
            showWarningToast({ message: t('objectives.form.invalidForm') });
        }
    };

    const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (isSubmittingRef.current) {
            event.preventDefault();
            return;
        }

        isSubmittingRef.current = true;
        handleSubmit(
            saveObjective,
            warnInvalidForm,
        )(event).finally(() => {
            isSubmittingRef.current = false;
        });
    };

    return {
        control,
        errors,
        isDirty,
        getValues,
        setValue,
        trigger,
        watch,
        formRef,
        handleFormSubmit,
        chooseSubmitStatus,
        isSaving: createMutation.isPending || updateMutation.isPending,
        savingStatus: readSavingStatus(),
        isHydrated,
        descriptionEditor: { key: descriptionEditorKey, initialValue: initialDescriptionValue },
        reseedDescription,
        savedResponsibles,
        selectedPeriodId,
        handlePeriodChange,
    };
};
